// Client telemetry: captures console output + uncaught browser errors and ships
// them to the backend (POST /api/telemetry/client-logs), tagged with browser_id
// (header) and the origin correlation_id, so Overseer ingests client-side failures
// alongside its server-side signals.
//
// Design constraints:
//  - bounded ring buffer (never grows without limit);
//  - batched + throttled flushing; immediate flush on errors and on page hide;
//  - transport via fetch (NOT the global axios instance) to avoid the 401-reload
//    interceptor; only sends when an auth token is present;
//  - backoff on failure;

import Cookies from "js-cookie";
import { getBrowserId } from "@/telemetry/browserId";
import { getLastCorrelationId } from "@/telemetry/correlation";

type ClientLogLevel = "log" | "debug" | "info" | "warn" | "error";

interface ClientLogEntry {
  level: ClientLogLevel;
  message: string;
  stack?: string;
  route?: string;
  correlation_id?: string | null;
  timestamp: string;
}

const ENDPOINT = "/api/telemetry/client-logs";
const LEVELS: ClientLogLevel[] = ["log", "debug", "info", "warn", "error"];
const MAX_BUFFER = 500;
const MAX_MESSAGE = 8 * 1024;
const BATCH_SIZE = 50;
// Smaller batch on the unload path: `keepalive` fetches share a hard ~64 KB
// total body cap across the page, and the browser silently drops requests that
// exceed it. Keeping this batch small ensures the final flush actually ships.
const KEEPALIVE_BATCH_SIZE = 10;
const FLUSH_INTERVAL = 10_000;
const BACKOFF_BASE = 15_000;
const BACKOFF_MAX = 5 * 60_000;

let buffer: ClientLogEntry[] = [];
let flushing = false;
let backoffUntil = 0;
let consecutiveFailures = 0;
let installed = false;

function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  const seen = new WeakSet<object>();
  try {
    return (
      JSON.stringify(value, (_key, val) => {
        if (typeof val === "bigint") return val.toString();
        if (typeof val === "object" && val !== null) {
          if (seen.has(val)) return "[Circular]";
          seen.add(val);
        }
        return val;
      }) ?? String(value)
    );
  } catch {
    return "[Unserializable]";
  }
}

function formatArgs(args: unknown[]): { message: string; stack?: string } {
  let stack: string | undefined;
  const parts = args.map((arg) => {
    if (arg instanceof Error) {
      if (!stack && arg.stack) stack = arg.stack;
      return `${arg.name}: ${arg.message}`;
    }
    return safeStringify(arg);
  });
  return { message: parts.join(" "), stack };
}

function push(level: ClientLogLevel, message: string, stack?: string): void {
  const entry: ClientLogEntry = {
    level,
    message: message.slice(0, MAX_MESSAGE),
    stack: stack ? stack.slice(0, MAX_MESSAGE) : undefined,
    route: window.location.pathname,
    correlation_id: getLastCorrelationId(),
    timestamp: new Date().toISOString(),
  };
  buffer.push(entry);
  if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
  if (level === "error" || buffer.length >= BATCH_SIZE) void flush();
}

function requeue(batch: ClientLogEntry[]): void {
  buffer = [...batch, ...buffer];
  if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
}

function scheduleBackoff(): void {
  consecutiveFailures += 1;
  const delay = Math.min(BACKOFF_BASE * 2 ** (consecutiveFailures - 1), BACKOFF_MAX);
  backoffUntil = Date.now() + delay;
}

async function flush(useKeepalive = false): Promise<void> {
  if (flushing || buffer.length === 0 || Date.now() < backoffUntil) return;

  // Only ship once authenticated; otherwise keep buffering (bounded) until login.
  const token = Cookies.get("access_token");
  if (!token) return;

  flushing = true;
  const batch = buffer.splice(0, useKeepalive ? KEEPALIVE_BATCH_SIZE : BATCH_SIZE);
  try {
    const response = await fetch(ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      keepalive: useKeepalive,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-Browser-ID": getBrowserId(),
        Authorization: token,
      },
      body: JSON.stringify({ logs: batch }),
    });
    if (response.ok) {
      consecutiveFailures = 0;
    } else {
      requeue(batch);
      scheduleBackoff();
    }
  } catch {
    // Never use console here — that would feed the capture loop. Just back off.
    requeue(batch);
    scheduleBackoff();
  } finally {
    flushing = false;
  }
}

function installConsole(): void {
  LEVELS.forEach((level) => {
    const original = console[level].bind(console);
    (console as unknown as Record<string, unknown>)[level] = (...args: unknown[]): void => {
      original(...args);
      try {
        const { message, stack } = formatArgs(args);
        push(level, message, stack);
      } catch {
        // logging must never break the app
      }
    };
  });
}

function installGlobalHandlers(): void {
  window.addEventListener("error", (event: ErrorEvent) => {
    try {
      const err = event.error;
      const message = err instanceof Error ? `${err.name}: ${err.message}` : String(event.message || "Uncaught error");
      const stack = err instanceof Error ? err.stack : `${event.filename}:${event.lineno}:${event.colno}`;
      push("error", message, stack);
    } catch {
      /* ignore */
    }
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason;
      const message =
        reason instanceof Error
          ? `Unhandled rejection: ${reason.name}: ${reason.message}`
          : `Unhandled rejection: ${safeStringify(reason)}`;
      const stack = reason instanceof Error ? reason.stack : undefined;
      push("error", message, stack);
    } catch {
      /* ignore */
    }
  });
}

function installUnloadFlush(): void {
  const onHide = (): void => void flush(true);
  window.addEventListener("pagehide", onHide);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") onHide();
  });
}

export function installClientLogger(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  installConsole();
  installGlobalHandlers();
  installUnloadFlush();
  window.setInterval(() => void flush(), FLUSH_INTERVAL);
}

installClientLogger();
